<script lang="ts">
  import { router } from '@inertiajs/svelte';
  import PublicProfileSubpageShell from '@/components/profile/PublicProfileSubpageShell.svelte';
  import type { ThemeConfig, ProfileThemeName } from '@/lib/profile-themes';

  interface Profile {
    display_name: string;
    username: string;
    avatar_initial: string;
    avatar_url?: string;
  }

  interface Theme extends ThemeConfig {
    template?: ProfileThemeName;
  }

  interface PostItem {
    id: string;
    title: string;
    content_preview: string;
    board_name?: string;
    likes_count: number;
    comments_count: number;
    created_at: string;
  }

  interface Pagination {
    current_page: number;
    total_pages: number;
    total_count: number;
  }

  let {
    profile,
    posts = [],
    pagination = { current_page: 1, total_pages: 1, total_count: 0 },
    theme = { template: 'clean' },
  }: {
    profile: Profile;
    posts: PostItem[];
    pagination: Pagination;
    theme?: Theme;
  } = $props();

  function formatDate(iso: string): string {
    return new Date(iso).toLocaleDateString('ko-KR', { year: 'numeric', month: 'short', day: 'numeric' });
  }

  function goToPage(page: number) {
    router.get(`/@${profile.username}/posts`, { page }, { preserveState: true });
  }
</script>

<svelte:head>
  <title>{profile.display_name}님의 게시글 | AX Hub</title>
  <meta name="description" content="{profile.display_name}님이 작성한 게시글 목록" />
</svelte:head>

<PublicProfileSubpageShell
  {profile}
  {theme}
  backHref={`/@${profile.username}`}
  title={`${profile.display_name}님의 게시글`}
  subtitle={`게시글 ${pagination.total_count}개`}
  width="4xl"
>
    {#if posts.length === 0}
      <div class="text-center py-16">
        <p class="public-subpage-faint text-lg">아직 작성한 게시글이 없습니다.</p>
      </div>
    {:else}
      <div class="space-y-3">
        {#each posts as post (post.id)}
          <a href="/posts/{post.id}"
            class="public-subpage-card block p-4 transition-all sm:p-5">
            <div class="flex items-start justify-between gap-3">
              <div class="min-w-0 flex-1">
                <h2 class="line-clamp-1 text-base font-semibold">{post.title}</h2>
                {#if post.content_preview}
                  <p class="public-subpage-muted mt-1 line-clamp-2 text-sm">{post.content_preview}</p>
                {/if}
              </div>
            </div>
            <div class="public-subpage-faint mt-3 flex items-center gap-3 text-xs">
              {#if post.board_name}
                <span class="public-subpage-pill inline-flex items-center px-2 py-0.5 text-xs font-medium">{post.board_name}</span>
              {/if}
              <span class="inline-flex items-center gap-1">
                <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
                {post.likes_count}
              </span>
              <span class="inline-flex items-center gap-1">
                <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                {post.comments_count}
              </span>
              <span>{formatDate(post.created_at)}</span>
            </div>
          </a>
        {/each}
      </div>

      {#if pagination.total_pages > 1}
        <div class="mt-8 flex items-center justify-center gap-3">
          <button onclick={() => goToPage(pagination.current_page - 1)}
            disabled={pagination.current_page <= 1}
            class="public-pagination-button px-4 py-2 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-40">이전</button>
          <span class="public-subpage-muted text-sm">{pagination.current_page} / {pagination.total_pages}</span>
          <button onclick={() => goToPage(pagination.current_page + 1)}
            disabled={pagination.current_page >= pagination.total_pages}
            class="public-pagination-button px-4 py-2 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-40">다음</button>
        </div>
      {/if}
    {/if}
</PublicProfileSubpageShell>

<style>
  .public-pagination-button {
    color: var(--text-primary, #111827);
    background: var(--card-bg, rgba(255, 255, 255, 0.92));
    border: 1px solid var(--card-border, rgba(15, 23, 42, 0.1));
    border-radius: 9999px;
    transition: background 150ms ease, border-color 150ms ease, transform 150ms ease;
  }

  .public-pagination-button:hover:not(:disabled) {
    transform: translateY(-1px);
    background: var(--card-hover, rgba(255, 255, 255, 0.96));
    border-color: color-mix(in srgb, var(--accent, #111827) 24%, transparent);
  }
</style>
